import type { DialogContentProps } from '@radix-ui/react-dialog';
import { forwardRef } from 'react';
import { styled, css, keyframes } from 'styled-components';
import { Content as DialogContent } from '@radix-ui/react-dialog';
import { DEFAULT_MODAL_WIDTH } from './constants';
import { mq } from '../../helpers/mq';
import { useFocusRestore } from '../../private/hooks/useFocusRestore';

type StyledModalContentProps = {
  $width?: string | undefined;
  $positionVariant: 'centered' | 'fixed-top' | 'right-side-panel';
};

const modalEnter = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(calc(var(--wui-modal-translate-y) + 24px));
  }

  to {
    opacity: 1;
    transform: translateX(-50%) translateY(var(--wui-modal-translate-y));
  }
`;

const modalExit = keyframes`
  from {
    opacity: 1;
    transform: translateX(-50%) translateY(var(--wui-modal-translate-y));
  }

  to {
    opacity: 0;
    transform: translateX(-50%) translateY(calc(var(--wui-modal-translate-y) + 24px));
  }
`;

const centeredModalStyles = css`
  --wui-modal-screen-offset: var(--wui-space-05);
  --wui-modal-translate-y: -50%;

  height: auto;
  max-height: calc(100vh - var(--wui-modal-screen-offset) * 2);
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(var(--wui-modal-translate-y));
  transform-origin: left center;
  animation: ${modalEnter} var(--wui-motion-duration-04) var(--wui-motion-ease-out);

  &[data-state='closed'] {
    animation: ${modalExit} var(--wui-motion-duration-03) var(--wui-motion-ease-out);
  }
`;

const fixedTopModalStyles = css`
  --wui-modal-screen-offset-top: 15vh;
  --wui-modal-screen-offset-bottom: 5vh;
  --wui-modal-translate-y: 0%;

  height: auto;
  max-height: calc(
    100vh - var(--wui-modal-screen-offset-top) - var(--wui-modal-screen-offset-bottom)
  );
  top: var(--wui-modal-screen-offset-top);
  left: 50%;
  transform: translateX(-50%) translateY(var(--wui-modal-translate-y));
  transform-origin: left center;
  animation: ${modalEnter} var(--wui-motion-duration-04) var(--wui-motion-ease-out);

  &[data-state='closed'] {
    animation: ${modalExit} var(--wui-motion-duration-03) var(--wui-motion-ease-out);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(100%);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideOutRight = keyframes`
  from {
    opacity: 1;
    transform: translateX(0);
  }

  to {
    opacity: 0;
    transform: translateX(100%);
  }
`;

const rightSidePanelModalStyles = css`
  --wui-modal-screen-offset: var(--wui-space-05);

  height: calc(100vh - var(--wui-modal-screen-offset) * 2);
  top: var(--wui-modal-screen-offset);
  right: var(--wui-modal-screen-offset);
  animation: ${slideInRight} var(--wui-motion-duration-05) var(--wui-motion-ease-out);
  transform-origin: right center;

  &[data-state='closed'] {
    animation: ${slideOutRight} var(--wui-motion-duration-03) var(--wui-motion-ease-in);
  }
`;

const positionStyleMap = {
  centered: centeredModalStyles,
  'fixed-top': fixedTopModalStyles,
  'right-side-panel': rightSidePanelModalStyles,
};

const StyledModalContent = styled(DialogContent)<StyledModalContentProps>`
  position: fixed;
  display: flex;
  flex-direction: column;
  gap: var(--wui-space-03);
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: var(--wui-zindex-modal);
  box-sizing: border-box;
  padding: var(--wui-space-05) 0;
  background-color: var(--wui-color-bg-app);

  ${mq.smAndUp} {
    position: fixed;
    top: unset;
    left: unset;
    width: calc(100vw - var(--wui-space-05) * 2);
    max-width: ${({ $width }) => $width ?? DEFAULT_MODAL_WIDTH};
    border-radius: var(--wui-border-radius-03);
    animation-duration: var(--wui-motion-duration-03);
    animation-timing-function: var(--wui-motion-ease-out);
    will-change: transform, opacity;
    ${({ $positionVariant }) => positionStyleMap[$positionVariant]}
  }
`;

type ModalContentProps = DialogContentProps & {
  width?: string | undefined;
  positionVariant: 'centered' | 'fixed-top' | 'right-side-panel';
};

// eslint-disable-next-line react/display-name
export const ModalContent = forwardRef<HTMLDivElement, ModalContentProps>(
  ({ width, positionVariant = 'fixed-top', children, ...props }, ref) => {
    useFocusRestore();

    return (
      <StyledModalContent
        ref={ref}
        $positionVariant={positionVariant}
        $width={width}
        {...props}
      >
        {children}
      </StyledModalContent>
    );
  },
);
